import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@components/atoms/button';
import { Loader } from '@components/atoms/loader';
import { Typography } from '@components/atoms/typography';
import { Header } from '@modules/dashboard/pages/kyc/docs-verification/docs-verification-page/header';
import { useMobileHeight } from '@modules/docs-verification-widget/hooks/use-mobile-height';
import type { GetVerificationResponse } from '@modules/docs-verification-widget/models/get-verification-response';
import { Box, Stack } from '@mui/material';
import { useAppSelector } from '@store';
import { useAppTheme } from '@theme';
import { getImageUrl } from '@utils/get-image-url';

import { useAutoRedirect } from './hooks/use-auto-redirect';
import { getCustomPartnerFinishSx } from './styles';

type CustomPartnerFinishProps = {
    verificationData?: GetVerificationResponse;
    redirectUrl?: string;
};

export const CustomPartnerFinish = ({
    verificationData,
    redirectUrl,
}: CustomPartnerFinishProps) => {
    const sdkOptions = useAppSelector((state) => state.docsVerification.sdkOptions);
    const isSkipIntro = sdkOptions?.isSkipIntro;
    const { minHeight } = useMobileHeight();
    const { t } = useTranslation();
    const theme = useAppTheme();
    const sx = useMemo(() => getCustomPartnerFinishSx(theme), [theme]);

    const verified = verificationData?.verified;
    const isInProgress = verified == null;
    const isSuccess = verified === true;
    const isFailed = verified === false;

    const imageSrc = getImageUrl(
        '/images/sdk/finish/cool-looking-dinosaur-guy-greetings-guests.png',
    );

    const { timeToRedirect, redirectToWallet } = useAutoRedirect(
        redirectUrl,
        isSuccess && !isSkipIntro,
    );

    if (isInProgress) {
        return (
            <Stack sx={sx.container} style={{ minHeight }}>
                <Header />
                <Box sx={sx.divider} />
                <Box sx={sx.loaderWrapper}>
                    <Loader text={t('docVerificationWidget.finish.verificationInProgress')} />
                </Box>
            </Stack>
        );
    }

    if (isSuccess) {
        return (
            <Stack sx={sx.container} style={{ minHeight }}>
                <Header />
                <Box sx={sx.divider} />
                <Stack sx={sx.content}>
                    <Typography tag='h2' variant='h2' color='text-main' sx={sx.title}>
                        {t('docVerificationWidget.finishCustomPartner.success.titleLine1')}
                    </Typography>
                    <Typography tag='p' variant='text' color='text-main' sx={sx.description}>
                        {t('docVerificationWidget.finishCustomPartner.success.description')}
                    </Typography>
                    {isSkipIntro ? (
                        <Typography
                            tag='p'
                            variant='text'
                            color='text-main'
                            sx={{
                                ...sx.description,
                                mt: 1,
                            }}
                        >
                            {t('docVerificationWidget.finishCustomPartner.returnToDesktop')}
                        </Typography>
                    ) : (
                        <Stack sx={sx.actions}>
                            <Button
                                variant='primary'
                                size='xl'
                                sx={sx.redirectButton}
                                onClick={redirectToWallet}
                            >
                                {t('docVerificationWidget.finishCustomPartner.returnToWallet')}
                            </Button>
                            <Typography
                                tag='p'
                                variant='text'
                                color='text-light'
                                sx={sx.redirectHint}
                            >
                                {t(
                                    'docVerificationWidget.finishCustomPartner.success.autoRedirect',
                                    {
                                        count: timeToRedirect / 1000,
                                    },
                                )}
                            </Typography>
                        </Stack>
                    )}
                    <Box component='img' sx={sx.image} src={imageSrc} />
                </Stack>
            </Stack>
        );
    }

    if (isFailed) {
        return (
            <Stack sx={sx.container} style={{ minHeight }}>
                <Header />
                <Box sx={sx.divider} />
                <Stack sx={sx.content}>
                    <Typography tag='h2' variant='h2' color='text-main' sx={sx.title}>
                        {t('docVerificationWidget.finishCustomPartner.failed.title')}
                    </Typography>
                    <Typography tag='p' variant='text' color='text-main' sx={sx.description}>
                        {t('docVerificationWidget.finishCustomPartner.failed.description')}
                    </Typography>
                    {isSkipIntro ? (
                        <Typography
                            tag='p'
                            variant='text'
                            color='text-main'
                            sx={{
                                ...sx.description,
                                mt: 1,
                            }}
                        >
                            {t('docVerificationWidget.finishCustomPartner.returnToDesktop')}
                        </Typography>
                    ) : (
                        <Button
                            variant='primary'
                            size='xl'
                            sx={sx.redirectButton}
                            onClick={redirectToWallet}
                        >
                            {t(
                                'docVerificationWidget.finishCustomPartner.returnToWalletAndTryAgain',
                            )}
                        </Button>
                    )}
                </Stack>
                <Box component='img' sx={sx.image} src={imageSrc} />
            </Stack>
        );
    }

    return null;
};
